/**
 * Stop a tab whose identity changed underneath it.
 *
 * The Auth0 token cache is in `localStorage`, shared by every tab of the
 * origin. When another tab signs in as someone else, or switches workspace or
 * organization, THIS tab's next `getAccessToken()` returns the other identity's
 * token — while its pages, its tenancy, its caches and its keyspaces still
 * belong to the old one. Requests go out as the new identity and their answers
 * render under the old one's header: one workspace's data shown as another's,
 * and cached under the old identity's keys.
 *
 * Streaming already refuses such a token (`tokenMatchesSession`) and quietly
 * stops. Nothing stopped the rest of the page. This does: it runs the same
 * check — on every change to the token cache and on a slow timer — and, on a
 * mismatch, drops the in-memory caches and covers the page with one choice:
 * reload into the identity the browser now has.
 *
 * A covering dialog rather than an automatic reload, so nothing typed is thrown
 * away without the person seeing why; not dismissable, because the page behind
 * it can no longer be trusted to show whose data it is.
 */
import * as React from "react";
import { tokenMatchesSession, type Session, type SessionManager } from "@authe/fe-commons/session";
import { useSession } from "@authe/ui-core/session";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@authe/ui-core";

/** How often to re-check without a storage event (a same-tab silent refresh). */
const CHECK_INTERVAL_MS = 30_000;

export function IdentityDriftGuard({ onDrift }: { onDrift?: () => void }): React.ReactElement | null {
  const { session, isAuthenticated, manager } = useSession();
  const drifted = useIdentityDrift(isAuthenticated ? session : null, manager, onDrift);
  if (!drifted) return null;
  return (
    <AlertDialog open>
      <AlertDialogContent onEscapeKeyDown={(event) => event.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle>You switched account or workspace in another tab</AlertDialogTitle>
          <AlertDialogDescription>
            This tab still shows the previous one, and anything it loads now would come from the new one.
            Reload to continue in the account and workspace you are signed in to.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => window.location.reload()}>Reload</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * `true` once the browser's live token no longer belongs to `session`.
 *
 * Sticky: once drifted, the tab stays stopped until it reloads — a token that
 * flips back does not make what was loaded in between trustworthy.
 */
export function useIdentityDrift(
  session: Session | null,
  manager: SessionManager,
  onDrift?: () => void,
): boolean {
  const [drifted, setDrifted] = React.useState(false);
  const onDriftRef = React.useRef(onDrift);
  React.useEffect(() => { onDriftRef.current = onDrift; });

  React.useEffect(() => {
    if (!session || drifted) return undefined;
    let active = true;
    // A sign-out or switch started in THIS tab is not drift: its own teardown
    // and navigation handle it.
    let leaving = false;
    const offIdentity = manager.subscribeIdentityChange(() => { leaving = true; });
    const check = async () => {
      const token = await manager.getAccessToken().catch(() => null);
      // No token is a signed-out or refreshing state the session layer owns.
      if (!active || leaving || !token) return;
      if (!tokenMatchesSession(token, session)) {
        onDriftRef.current?.();
        setDrifted(true);
      }
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key.startsWith("@@auth0spajs@@")) void check();
    };
    window.addEventListener("storage", onStorage);
    const timer = window.setInterval(() => { void check(); }, CHECK_INTERVAL_MS);
    return () => {
      active = false;
      offIdentity();
      window.removeEventListener("storage", onStorage);
      window.clearInterval(timer);
    };
  }, [session, manager, drifted]);

  return drifted;
}
